import {AlertFillIcon, AlertIcon, CheckCircleFillIcon, QuestionIcon} from '@primer/octicons-react'

import {normalizeConnectionStatus, type ConnectionStatus, type NormalizedStatus} from '../types/connectors'
import styles from './ConnectionStatusIcon.module.css'

export interface ConnectionStatusIconProps {
  status?: ConnectionStatus
  size?: number
}

type Presentation = {
  Icon: typeof CheckCircleFillIcon
  label: string
  className: string | undefined
}

const PRESENTATION: Record<Exclude<NormalizedStatus, 'not_connected'>, Presentation> = {
  connected: {Icon: CheckCircleFillIcon, label: 'Connected', className: styles.connected},
  pending: {Icon: AlertIcon, label: 'Setup incomplete', className: styles.pending},
  error: {Icon: AlertFillIcon, label: 'Connection needs attention', className: styles.error},
  unknown: {Icon: QuestionIcon, label: 'Connection status unknown', className: styles.unknown},
}

/**
 * Icon and color for a status, for callers that render their own visible text.
 * Null when not connected, which carries no indicator.
 */
export function connectionStatusPresentation(status?: ConnectionStatus): Presentation | null {
  const normalized = normalizeConnectionStatus(status)
  if (normalized === 'not_connected') return null
  return PRESENTATION[normalized]
}

/** Trailing status indicator for a connector row. Renders nothing when not connected. */
export function ConnectionStatusIcon({status, size = 16}: ConnectionStatusIconProps) {
  const presentation = connectionStatusPresentation(status)
  if (!presentation) return null

  const {Icon, label, className} = presentation

  return (
    <span className={className} data-testid="connection-status-icon">
      <Icon size={size} aria-label={label} />
    </span>
  )
}

/** Human readable status text, for the detail dialog and screen readers. */
export function connectionStatusLabel(status?: ConnectionStatus): string {
  switch (normalizeConnectionStatus(status)) {
    case 'connected':
      return 'Connected'
    case 'pending':
      return 'Setup incomplete'
    case 'error':
      return 'Needs attention'
    case 'not_connected':
      return 'Not connected'
    default:
      return 'Unknown'
  }
}
